import { useMutation, useQuery } from '@tanstack/react-query'
import { UsersRes } from '@/types/user'
import { axiosInstance } from '@/lib/axios'

export interface UsersProps {
  role: 'student' | 'tutor' | 'all' | string
  classId?: string
  search: string
  page: number
  limit: number
  is_active: string
  unassigned: string
  type: string
}

export const getUsers = async ({
  role,
  classId,
  search,
  page,
  limit = 10,
  is_active,
  unassigned,
  type,
}: UsersProps) => {
  const params: Record<string, string | number | boolean> = {
    page,
    size: limit,
  }

  if (type && type !== 'all') {
    params.created_type = type.toLocaleUpperCase()
  }
  if (unassigned && unassigned !== 'all') {
    params.unassigned = unassigned === 'unassigned'
  }
  if (is_active && is_active !== 'all') {
    params.is_active = is_active === 'active'
  }
  if (role && role !== 'all') {
    params.role = role
  }

  if (classId && classId !== 'all') {
    params.class_id = classId
  }

  if (search && search.trim() !== '') {
    params.search = search
  }

  return axiosInstance.get<UsersRes>(`/user/`, { params })
}

export const deleteUser = async (id: string) => {
  return axiosInstance.post(`/user/deactivate?user_id=${id}`)
}

export const useGetUsers = ({
  role,
  classId,
  search,
  page,
  limit = 10,
  is_active,
  unassigned,
  type,
}: UsersProps) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'users',
      role,
      classId,
      search,
      page,
      limit,
      is_active,
      unassigned,
      type,
    ],
    queryFn: () =>
      getUsers({
        role,
        classId,
        search,
        page,
        limit,
        is_active,
        unassigned,
        type,
      }),
  })

  return {
    users: data?.data.data ?? [],
    meta: data?.data.meta,
    isLoadingUsers: isLoading,
    refetchUsers: refetch,
  }
}

export const useDeleteUser = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationKey: ['delete-user'],
    mutationFn: deleteUser,
  })

  return { deleteUserAsync: mutateAsync, isDeleting: isPending }
}
